import uniq from 'lodash.uniq';

import defineThemeMiddleware from '#shared/util/defineThemeMiddleware.js';
import updateStoreData from '#shared/util/updateStoreData.js';
import { getQueryAll } from '#shared/util/url.js';
import removeFalsy from '#shared/util/removeFalsy.js';
import isString from '#shared/util/isString.js';
import { accountSchema } from '#shared/schema/my/account.js';

/**
 * @typedef {defineThemeMiddleware.ThemeMiddlewareContext} ThemeMiddlewareContext
 */

/**
 * @typedef {import('vue').VueConstructor} Vue
 * @property {object} tracking
 */

/**
 * @typedef {'AccountMismatch'|'AccountMismatchCase'|'AccountMismatchEmail'|'AccountMismatchError'|'AccountMismatchGuest'|'AccountMismatchNull'|'AccountMismatchProvider'} AccountMismatchKind
 */

/**
 * @typedef {object} AccountMismatchLog
 * @property {object} mismatch
 * @property {string} mismatch.appEmail
 * @property {string} mismatch.accountEmail
 * @property {string} mismatch.providerEmail
 * @property {object} session
 * @property {string} session.id
 * @property {string} session.origin
 */

/**
 * Make AccountMismatch log (GA, WebSocket)
 * @param {Vue} app
 * @param {AccountMismatchKind} kind
 * @param {AccountMismatchLog} logObject
 * @returns {Promise<void>}
 */
const logAccountMismatch = async (app, kind, logObject) => {
	app.tracking.accountMismatch(kind, logObject.session.id);
	await app.websocket.send('POST /log', {
		message: kind,
		origin: 'openCartMiddleware',
		detail: logObject
	});
};

/**
 * Handle middleware on the server side
 * @param {object} options
 * @param {boolean} options.isOpenCartFlow
 * @param {object} options.cartParameters
 * @param {ThemeMiddlewareContext} context
 * @returns {Promise}
 */
const serverFlow = async ({ isOpenCartFlow, cartParameters }, { store, request, response }) => {
	// If we are on server side and there is no new cart request just return current cart data
	if (!isOpenCartFlow) {
		await store.dispatch('getCart');
		return;
	}
	// Load variables from request locals for future use
	const { regionConfig, sessionStore } = response.locals;
	// Load provider schemas
	const { customerSchema } = await import(`../schema/${regionConfig.provider.name}/customer.js`);
	const { paymentSchema } = await import(`../schema/${regionConfig.provider.name}/payment.js`);
	const { orderSchema } = await import(`../schema/${regionConfig.provider.name}/order.js`);
	const { providerAccountSchema } = await import(`../schema/${regionConfig.provider.name}/account.js`);
	// Set initial Vuex store data
	const account = accountSchema({});
	const customer = customerSchema({
		cart: {}
	});
	const payment = paymentSchema({
		sessionStore,
		regionConfig,
		cart: {},
		customIDs: cartParameters.customIDs
	});
	const order = orderSchema({
		payment,
		sessionStore,
		request,
		response,
		regionConfig,
		cart: {},
		orderedProducts: []
	});
	const providerAccount = providerAccountSchema({});
	updateStoreData(store, {
		account,
		customer,
		payment,
		order,
		providerAccount
	});
};

/**
 * Handle middleware on the client side
 * @param {object} options
 * @param {boolean} options.isOpenCartFlow
 * @param {object} options.cartParameters
 * @param {ThemeMiddlewareContext} context
 * @returns {Promise}
 */
const clientFlow = async ({ isOpenCartFlow, cartParameters }, { app, store }) => {
	// If we are on client side and there is a request to open new cart, grab the cart open data and call the API
	if (isOpenCartFlow) {
		// Push "cartRequest" event into Google Analytics
		app.tracking.cartRequest();
		// Call open cart model to load the cart
		const openCartResponse = await store.dispatch('openCart', cartParameters);
		// Validate authenticated cart opens
		const { isDeveloper, isGuest, isAuthenticated } = openCartResponse.data.order;
		const expectAuthenticated = !isDeveloper && isString(cartParameters.accountIdTimestamp, 1) && store.state.theme.tracking?.platform === 'ipm';
		const accountFromClient = expectAuthenticated ? await app.getAccountFromClient() : {};
		const appEmail = accountFromClient?.email;
		const accountEmail = openCartResponse.data.account?.email;
		const providerEmail = openCartResponse.data.providerAccount?.email;
		// AccountMismatchGuest - fallback from authenticated to guest
		const isAccountMismatchGuest = expectAuthenticated && isGuest;
		// AccountMismatchError - method does not exist
		const isAccountMismatchError = expectAuthenticated && isAuthenticated && typeof appEmail === 'undefined';
		// AccountMismatchNull - e-mail address missing in client app (e.g. client not logged in)
		const isAccountMismatchNull = expectAuthenticated && isAuthenticated && typeof appEmail === 'string' && appEmail.length === 0;
		// AccountMismatchProvider - account e-mail address doesn't match the data from provider account
		const isAccountMismatchProvider = expectAuthenticated && isAuthenticated && isString(accountEmail, 1) && isString(providerEmail, 1) && accountEmail.toLowerCase() !== providerEmail.toLowerCase();
		// AccountMismatchEmail - account e-mail address doesn't match the data from client app
		const isAccountMismatchEmail = expectAuthenticated && isAuthenticated && isString(accountEmail, 1) && isString(appEmail, 1) && accountEmail.toLowerCase() !== appEmail.toLowerCase();
		// AccountMismatchCase - some of account, provider or client app email address doesn't match
		const isAccountMismatchCase = expectAuthenticated && isAuthenticated && isString(accountEmail, 1) && isString(providerEmail, 1) && isString(appEmail, 1) && uniq([accountEmail, providerEmail, appEmail]).length > 1;
		// Log account mismatch events
		const accountMismatchLog = {
			mismatch: {
				appEmail: appEmail ?? '-',
				accountEmail: accountEmail ?? '-',
				providerEmail: providerEmail ?? '-'
			},
			session: openCartResponse.data.session
		};
		if (isAccountMismatchGuest) {
			await logAccountMismatch(app, 'AccountMismatchGuest', accountMismatchLog);
		} else if (isAccountMismatchError) {
			await logAccountMismatch(app, 'AccountMismatchError', accountMismatchLog);
		} else if (isAccountMismatchNull) {
			await logAccountMismatch(app, 'AccountMismatchNull', accountMismatchLog);
		} else if (isAccountMismatchProvider) {
			await logAccountMismatch(app, 'AccountMismatchProvider', accountMismatchLog);
		} else if (isAccountMismatchEmail) {
			await logAccountMismatch(app, 'AccountMismatchEmail', accountMismatchLog);
		} else if (isAccountMismatchCase) {
			await logAccountMismatch(app, 'AccountMismatchCase', accountMismatchLog);
		}
		// Create "summary" log in case of application account email and provider or checkout account emails are not equal
		if (isAccountMismatchGuest || isAccountMismatchError || isAccountMismatchNull || isAccountMismatchProvider || isAccountMismatchEmail || isAccountMismatchCase) {
			await logAccountMismatch(app, 'AccountMismatch', accountMismatchLog);
		}
		// Update store state with response from checkout API
		updateStoreData(store, openCartResponse.data);
		// Set checkout session token and object required to make following calls
		store.commit('SET_APP', {
			token: openCartResponse.data.token,
			session: openCartResponse.data.session
		});
		// We need to set selected payment method type in order to load provider inputs correctly
		if (store.state.order.isAuthenticated) {
			store.commit('SET_SELECTED_PAYMENT', {
				type: store.state.payment.type
			});
		}
		// Update query after we have the cart data
		await app.updateUrlQuery();
		// Remember initial customer and form data (for example GeoIP from open cart request)
		app.rememberForm('customer', 'SET_CUSTOMER', store.state.customer);
		app.rememberForm('form', 'SET_FORM', store.state.form);
	}
	// Load and initialize libraries
	await app.providersLibrary.load(store);
};

/**
 * Handle cart open
 */
export default defineThemeMiddleware(async (context) => {
	// Get cart parameters object
	const cartParameters = removeFalsy({
		accountIdTimestamp: context.route.query.aid,
		additional2: context.route.query.additional2,
		afbData: context.route.query.afbData,
		agentDesignation: context.route.query.agentDesignation,
		aswparam: context.route.query.aswparam,
		avgRenewalFlag: context.route.query.avgRenewalFlag,
		brandName: context.store.state.url.brand,
		browserInformation: context.isClientProcess && {
			acceptHeader: navigator.userAgent,
			colorDepth: screen.colorDepth,
			javaEnabled: true,
			language: navigator.language,
			screenHeight: screen.height,
			screenWidth: screen.width,
			timeZoneOffset: new Date().getTimezoneOffset(),
			userAgent: navigator.userAgent
		},
		campaign: context.route.query.campaign,
		campaignMarker: context.route.query.campaignMarker,
		consentGroups: context.store.state.theme.tracking.consentGroups,
		customIDs: getQueryAll(context.store.state.url.href, 'customID', []),
		environment: context.route.query.env,
		isDebug: context.route.query.debugBar === '1',
		isDeveloper: context.route.query.developer === '1',
		isTest: context.route.query.testOrder === '1',
		linkHash: context.route.query.h,
		linkTimestamp: context.route.query.t ? Number(context.route.query.t) : null,
		locale: context.store.state.url.locale,
		myAppContext: context.route.query.myAppContext,
		orderSource: context.route.query.orderSource,
		products: getQueryAll(context.store.state.url.href, 'product', []),
		providerAccountId: context.route.query.shopper,
		providerName: context.route.query.provider,
		quantities: getQueryAll(context.store.state.url.href, 'quantity', []).map(Number),
		retargetingUrl: context.store.state.url.href,
		themeName: context.store.state.url.theme,
		trackingDisabled: context.store.state.theme.tracking.disabled,
		useHotjar: context.store.state.theme.tracking.hotjar
	});
	// Define variable to determine if new cart should be loaded
	const isOpenCartFlow = context.route.query.clearCart === '1';
	// Run the client or server flows and expect cart data to return (not required)
	await (context.isClientProcess ? clientFlow : serverFlow)({
		isOpenCartFlow,
		cartParameters
	}, context);
});
